import { Avatar, Group, Loader, Text } from '@mantine/core';
import { useGetUserByCognitoID } from 'api/resource';
import { getUserFullName } from 'hooks/useGetUsersDisplayInfo';
import { formatTimeOnly, nowISO, parseDateTime } from 'utils/dateUtils';
import { useCurrentSiteStore } from 'store';
import { DateTime } from 'luxon';
import { useTranslation } from '@refinedev/core';
import { formatDateTime } from 'utils/dateFormatting';

export const ChangedByUser: React.FC<{
  cognitoID: string;
  updatedAt?: string;
  variant?: 'legacy' | 'compact';
}> = ({ cognitoID, updatedAt, variant = 'legacy' }) => {
  const isAuto = cognitoID === 'auto';

  const { data: user, isLoading } = useGetUserByCognitoID(
    !isAuto ? cognitoID : null
  );

  if (isLoading) {
    return <Loader />;
  }

  const userName = user ? getUserFullName(user) : 'Unknown User';

  if (variant === 'compact') {
    return (
      <ChangedByCompact
        userName={isAuto ? 'System Auto-Biller' : userName}
        updatedAt={updatedAt}
      />
    );
  }

  return isAuto ? (
    <ChangedBySystem updatedAt={updatedAt} />
  ) : (
    <ChangedBy userName={userName} updatedAt={updatedAt} />
  );
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'U';

const ChangedByCompact: React.FC<{
  userName: string;
  updatedAt?: string;
}> = ({ userName, updatedAt }) => {
  const { timezone } = useCurrentSiteStore();
  const { translate } = useTranslation();
  const displayTime = formatDateTime(updatedAt, {
    timezone,
    format: 'yyyy/MM/dd HH:mm',
    fallback: translate('pages:claims.historyUnknownTime', 'Unknown time'),
  });

  return (
    <Group gap={8} wrap="nowrap">
      <Avatar size={20} radius="xl" color="indigo">
        <Text size="xs" fw={700}>
          {getInitials(userName)}
        </Text>
      </Avatar>
      <Text size="sm" c="dimmed">
        {userName}
      </Text>
      <Text size="sm" c="dimmed">
        ·
      </Text>
      <Text size="sm" c="dimmed">
        {displayTime}
      </Text>
    </Group>
  );
};

const ChangedBySystem: React.FC<{ updatedAt?: string }> = ({ updatedAt }) => {
  const { timezone } = useCurrentSiteStore();

  let display = '';
  if (updatedAt) {
    const dt = parseDateTime(updatedAt, { zone: timezone });
    if (dt.isValid) {
      const now = parseDateTime(nowISO(timezone), { zone: timezone });

      const timePart = formatTimeOnly(dt, {
        timezone,
        format: DateTime.TIME_SIMPLE,
      });
      const datePart = dt.hasSame(now, 'day')
        ? 'Today'
        : dt.toFormat('ccc LLL d'); // e.g. "Mon Sep 5"

      display = `${datePart}, ${timePart} - Billing System`;
    } else {
      display = `Unknown DateTime - Billing System`;
    }
  }
  return (
    <Text component="span" fw={'bold'}>
      {display}
    </Text>
  );
};

const ChangedBy: React.FC<{
  userName: string;
  updatedAt?: string;
}> = ({ userName, updatedAt }) => {
  const { timezone } = useCurrentSiteStore();
  let display = '';
  if (updatedAt) {
    const dt = parseDateTime(updatedAt, { zone: timezone });
    if (dt.isValid) {
      const now = parseDateTime(nowISO(timezone), { zone: timezone });

      const timePart = formatTimeOnly(dt, {
        timezone,
        format: DateTime.TIME_SIMPLE,
      });
      const datePart = dt.hasSame(now, 'day')
        ? 'Today'
        : dt.toFormat('ccc LLL d'); // e.g. "Mon Sep 5"

      display = `${datePart}, ${timePart} - ${userName}`;
    } else {
      display = `Unknown DateTime - ${userName}`;
    }
  }

  return (
    <Text component="span" fw="bold">
      {display}
    </Text>
  );
};
